import { implicitGroupTitle, type ParamSpec } from "@cfg/design-sdk";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useConfiguratorContext, useConfiguratorStore } from "./context.tsx";
import { useDesign, useMesh, useParams } from "./hooks.ts";
import { useLocale } from "./locale.tsx";
import { scrollBehavior } from "./timing.ts";
import { ParamField, type FieldIssue } from "./widgets.tsx";

interface Group {
  id: string;
  title: string;
  params: string[];
  collapsed?: boolean;
}

/**
 * Split the spec into the design's declared groups plus a "General" group for the rest.
 * `text.general` names that group; `text.groupTitle` titles groups that params name but `groups` doesn't list.
 */
export function groupSpecs(
  spec: ParamSpec[],
  groups: Group[] | undefined,
  order?: string[],
  text: { general?: string; groupTitle?: (title: string) => string } = {},
): { group: Group; specs: ParamSpec[] }[] {
  const byName = new Map(spec.map((s) => [s.name, s]));
  const used = new Set<string>();
  const out: { group: Group; specs: ParamSpec[] }[] = [];
  for (const g of groups ?? []) {
    const specs = g.params.map((n) => byName.get(n)).filter((s): s is ParamSpec => !!s);
    specs.forEach((s) => used.add(s.name));
    if (specs.length) out.push({ group: g, specs });
  }
  // params that declared a group via meta but were not listed
  for (const s of spec) {
    if (used.has(s.name) || !s.group) continue;
    const groupId = s.group;
    let bucket = out.find((o) => o.group.id === groupId);
    if (!bucket) {
      const title = implicitGroupTitle(groupId);
      bucket = { group: { id: groupId, title: text.groupTitle ? text.groupTitle(title) : title, params: [] }, specs: [] };
      out.push(bucket);
    }
    bucket.specs.push(s);
    used.add(s.name);
  }
  const rest = spec.filter((s) => !used.has(s.name));
  if (rest.length) out.unshift({ group: { id: "main", title: text.general ?? "General", params: rest.map((r) => r.name) }, specs: rest });
  if (order?.length) {
    const rank = new Map(order.map((n, i) => [n, i]));
    for (const o of out) o.specs.sort((a, b) => (rank.get(a.name) ?? 1e9) - (rank.get(b.name) ?? 1e9));
  }
  return out;
}

export interface ParamFormProps {
  /** Hide params flagged `advanced` unless toggled. */
  showAdvanced?: boolean;
  disabled?: boolean;
  /** Rendered above the fields (presets, etc.). */
  header?: ReactNode;
  footer?: ReactNode;
  validationErrors?: Record<string, string>;
  /**
   * Mark params named by the design's validate() issues (design studio). Off by default:
   * shoppers see no print check, since every param combination should print (`design build` reports the print check).
   */
  showIssues?: boolean;
}

/** DOM id of the inline print-check message for a param (target of the field's `aria-describedby`). */
export const issueIdFor = (param: string) => `cfg-issue-${param}`;

/**
 * Generated parameter form: groups become collapsible sections, each param its widget.
 *
 * Group bodies stay mounted when collapsed: `.cfg-group[data-state=open|closed]` >
 * `.cfg-group-collapse` (grid rows 0fr↔1fr, `inert` when closed) > `.cfg-group-clip` >
 * `.cfg-group-body`, so CSS can animate both ways. With `showIssues`, params with print-check
 * issues get `.is-invalid` (errors) / `data-issue=<level>` and an inline `.cfg-field-issue` message.
 */
export function ParamForm({ showAdvanced = false, disabled, header, footer, validationErrors, showIssues = false }: ParamFormProps) {
  const { description, spec } = useDesign();
  const { params, setParam, setActive } = useParams();
  const { issues } = useMesh();
  const focusRequest = useConfiguratorStore((s) => s.focusRequest);
  const { copy, designs } = useLocale();
  const groups = useMemo(
    () =>
      groupSpecs(spec, description?.ui?.groups as Group[] | undefined, description?.ui?.order as string[] | undefined, {
        general: copy("configurator.groupGeneral"),
        groupTitle: designs && description ? (title) => designs.text(description.id, title) : undefined,
      }),
    [spec, description, copy, designs],
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [forceAdvanced, setForceAdvanced] = useState<Set<string>>(() => new Set());
  const issuesByParam = useMemo(() => {
    const m = new Map<string, FieldIssue>();
    if (!showIssues) return m;
    const rank = { error: 3, warning: 2, info: 1 } as const;
    for (const i of issues) {
      if (!i.param) continue;
      const prev = m.get(i.param);
      if (!prev || rank[i.level] > rank[prev.level]) m.set(i.param, { level: i.level, message: i.message });
    }
    return m;
  }, [issues, showIssues]);

  // Reveal + focus a field on request (issue links, floating labels): open its group first.
  const lastFocus = useRef(0);
  useEffect(() => {
    if (!focusRequest || focusRequest.n === lastFocus.current) return;
    lastFocus.current = focusRequest.n;
    const { name } = focusRequest;
    const owner = groups.find((g) => g.specs.some((s) => s.name === name));
    if (owner) setOpen((o) => ({ ...o, [owner.group.id]: true }));
    const target = owner?.specs.find((s) => s.name === name);
    if (target?.advanced && !showAdvanced) setForceAdvanced((f) => new Set(f).add(name));
    // Wait a frame so an opening group is laid out (and not inert) before focusing.
    const raf = requestAnimationFrame(() => revealField(name));
    return () => cancelAnimationFrame(raf);
  }, [focusRequest, groups, showAdvanced]);

  if (!description) return null;
  return (
    <form className="cfg-form" onSubmit={(e) => e.preventDefault()}>
      {header}
      {groups.map(({ group, specs }) => {
        const visible = specs.filter((s) => showAdvanced || !s.advanced || forceAdvanced.has(s.name));
        if (!visible.length) return null;
        const collapsible = groups.length > 1;
        const isOpen = !collapsible || (open[group.id] ?? !group.collapsed);
        const bodyId = `cfg-group-${group.id}`;
        const hasError = visible.some((s) => issuesByParam.get(s.name)?.level === "error");
        return (
          <section key={group.id} className={`cfg-group${isOpen ? " is-open" : ""}${hasError ? " has-error" : ""}`} data-state={isOpen ? "open" : "closed"} data-group={group.id}>
            {collapsible && (
              <button type="button" className="cfg-group-toggle" aria-expanded={isOpen} aria-controls={bodyId} onClick={() => setOpen({ ...open, [group.id]: !isOpen })}>
                <span>{group.title}</span>
                <span className="cfg-group-chevron" aria-hidden>
                  {isOpen ? "−" : "+"}
                </span>
              </button>
            )}
            <div className="cfg-group-collapse" id={bodyId} inert={!isOpen}>
              <div className="cfg-group-clip">
                <div className="cfg-group-body">
                  {visible.map((s) => (
                    <ParamField
                      key={s.name}
                      spec={s}
                      value={params[s.name]}
                      disabled={disabled}
                      invalid={validationErrors?.[s.name]}
                      issue={issuesByParam.get(s.name)}
                      onChange={(v) => setParam(s.name, v)}
                      onFocus={() => setActive(s.name)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>
        );
      })}
      {footer}
    </form>
  );
}

/** Scroll a param's field into view (smooth unless reduced motion) and focus its first control. */
export function revealField(name: string): boolean {
  if (typeof document === "undefined") return false;
  const el = document.getElementById(`cfg-param-${name}`);
  if (!el) return false;
  el.scrollIntoView({ behavior: scrollBehavior(), block: "center" });
  const input = el.querySelector<HTMLElement>("input:not([tabindex='-1']):not([type=range]),select,textarea,button");
  input?.focus({ preventScroll: true });
  return true;
}

/** Preset chips; hidden when the design ships none. */
export function PresetBar({ label: labelProp }: { label?: string }) {
  const { description } = useDesign();
  const copy = useLocale().copy;
  const label = labelProp ?? copy("configurator.presets");
  const { applyPreset } = useParams();
  const presets = (description?.ui?.presets ?? []) as { id: string; title: string; note?: string }[];
  if (!presets.length) return null;
  return (
    <div className="cfg-presets" role="group" aria-label={label}>
      <span className="cfg-presets-label">{label}</span>
      {presets.map((p) => (
        <button key={p.id} type="button" className="cfg-chip" title={p.note} onClick={() => applyPreset(p.id)}>
          {p.title}
        </button>
      ))}
    </div>
  );
}

export interface IssueListProps {
  title?: string;
  /** Element id (target for "Fix … ↑" links). Default `cfg-issues`. */
  id?: string;
  className?: string;
  /** Only these levels (e.g. `["error"]` for a compact list next to the actions). */
  levels?: ("error" | "warning" | "info")[];
  /** Make issues that name a param clickable: reveals and focuses that field. Default true. */
  linkParams?: boolean;
}

/**
 * Print-check issues from the design's validate() and the kernel, for the design studio
 * (the shell shows none). Each item carries `.cfg-issue.is-<level>` + `data-level` /
 * `data-param`; items with a param render a `.cfg-issue-link` button that reveals the
 * field. The container is focusable (tabIndex -1). Renders nothing without issues.
 */
export function IssueList({ title: titleProp, id = "cfg-issues", className, levels, linkParams = true }: IssueListProps) {
  const { issues: all } = useMesh();
  const copy = useLocale().copy;
  const title = titleProp ?? copy("configurator.warnings");
  const { store } = useConfiguratorContext();
  const issues = levels ? all.filter((i) => levels.includes(i.level)) : all;
  if (!issues.length) return null;
  const worst = issues.some((i) => i.level === "error") ? "error" : issues.some((i) => i.level === "warning") ? "warning" : "info";
  return (
    <div className={`cfg-issues${className ? ` ${className}` : ""}`} id={id} tabIndex={-1} data-level={worst}>
      <div className="cfg-issues-title">{title}</div>
      <ul>
        {issues.map((i, n) => (
          <li key={n} className={`cfg-issue is-${i.level}`} data-level={i.level} data-param={i.param}>
            {i.param && linkParams ? (
              <button type="button" className="cfg-issue-link" onClick={() => store.getState().requestFocus(i.param!)}>
                {i.message}
              </button>
            ) : (
              i.message
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
